import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { EmailIcon, ExclamationMarkIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { OtoIndividualPlacement } from '../../../lib/types/models/enrollment/oto-individual-enrollments'
import { HostFamilyProperties } from '../../../lib/types/models/host-family/properties.host-family'
import InformationToolTip from '../../elements/information-tooltip'
import OlCenterHostFamilyPlacementHistory from '../ol-common/ol-center-placement-enrollment-table/ol-center-host-family-placement-history'

type Props = {
  placement: OtoIndividualPlacement
  // the provider gets the assign / change action, LEC only reads the placement
  onActionClick?: (placement: OtoIndividualPlacement) => void
  // LEC shows the placement history next to the family, the provider has a dedicated column for it
  withHistory?: boolean
}

const OtoIndividualHostFamilyCell = ({ placement, onActionClick, withHistory }: Props) => {
  const { t } = useTranslate()
  const { userRole } = useAuth()
  const { navigate } = useNavigator()

  const { hostFamily, informationSharedToHFByEmail, hostFamilyHistory } =
    placement.otoIndividualProposition
  const isOl = userRole === RoleEnum.Ol

  if (!hostFamily && !onActionClick) return '-'

  // the provider names the family by its family name, LEC identifies it by the main parent
  const name = !hostFamily
    ? ''
    : isOl
      ? hostFamily.familyName
      : toFullName(hostFamily.parent1FirstName, hostFamily.parent1LastName, {
          options: { mode: 'lastname-firstname' }
        })

  return (
    <div className="flex items-center gap-4 py-2">
      <div className="flex flex-col gap-1">
        {hostFamily && (
          <div className="flex items-center gap-1">
            <Typography.Link
              variant="label-semibold"
              color={tailwindColors.vividBlue.DEFAULT}
              onClick={(e) => {
                e.stopPropagation()
                navigate(
                  (isOl
                    ? Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_UPDATE_PAGE
                    : Paths.OL_HOST_FAMILY_INFORMATION
                  ).replace(':id', hostFamily.id.toString())
                )
              }}
            >
              {name}
            </Typography.Link>

            {informationSharedToHFByEmail && (
              <InformationToolTip
                contentVariant="secondary"
                align="center"
                size="small"
                icon={
                  <div className="flex items-center justify-center bg-grey-light w-fit p-1 rounded-full">
                    <EmailIcon color={tailwindColors.grey.DEFAULT} height="15" width="15" />
                  </div>
                }
              >
                <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
                  {t('oto.individual.placements.host.family.info.emailed')}
                </Typography>
              </InformationToolTip>
            )}
          </div>
        )}

        {onActionClick && (
          <Button
            type="button"
            size="small"
            label={
              hostFamily
                ? t('oto.individual.placements.host.family.change')
                : t('oto.individual.placements.host.family.assign')
            }
            variant={hostFamily ? 'secondary' : 'ghost'}
            onClick={() => onActionClick(placement)}
          />
        )}
      </div>

      <div className="flex items-center gap-1">
        {HostFamilyProperties.hasVisibleLecComment(hostFamily, userRole) && (
          <div className="flex items-center justify-center w-6 h-6 bg-red-light rounded-full">
            <div className="flex items-center justify-center h-4 w-4 rounded-full bg-red">
              <ExclamationMarkIcon filled color={tailwindColors.white.DEFAULT} />
            </div>
          </div>
        )}

        {withHistory && hostFamilyHistory.length > 0 && (
          <OlCenterHostFamilyPlacementHistory hostFamilyHistory={hostFamilyHistory} />
        )}
      </div>
    </div>
  )
}

export default OtoIndividualHostFamilyCell
